import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import { useEffect, useRef, useState } from 'react';
import type { RectCords } from 'folds';
import { Header, IconButton, Input, Menu, MenuItem, Text, config } from 'folds';
import { PopOut } from '$components/overlay-stack';
import { composerIcon, CaretDown } from '$components/icons/phosphor';
import FocusTrap from 'focus-trap-react';

import { useDebounce } from '$hooks/useDebounce';
import { stopPropagation } from '$utils/keyboard';

export function ServerPicker({
  server,
  serverList,
  allowCustomServer,
  onServerChange,
}: {
  server: string;
  serverList: string[];
  allowCustomServer?: boolean;
  onServerChange: (server: string) => void;
}) {
  const [serverMenuAnchor, setServerMenuAnchor] = useState<RectCords>();
  const serverInputRef = useRef<HTMLInputElement>(null);
  const inputFocusedRef = useRef(false);

  useEffect(() => {
    // sync input with outside server changes, but only when not focused
    // (on iOS, syncing while focused causes the deleted text to reappear)
    if (
      !inputFocusedRef.current &&
      serverInputRef.current &&
      serverInputRef.current.value !== server
    ) {
      serverInputRef.current.value = server;
    }
  }, [server]);

  const debounceServerSelect = useDebounce(onServerChange, { wait: 700 });

  const handleServerChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
    const inputServer = evt.target.value.trim();
    if (inputServer) debounceServerSelect(inputServer);
  };

  const handleInputFocus: FocusEventHandler<HTMLInputElement> = () => {
    inputFocusedRef.current = true;
  };

  const handleInputBlur: FocusEventHandler<HTMLInputElement> = () => {
    inputFocusedRef.current = false;
    // restore the server value if the input was cleared without selecting a new one
    if (serverInputRef.current && serverInputRef.current.value.trim() === '') {
      serverInputRef.current.value = server;
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (evt) => {
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      setServerMenuAnchor(undefined);
    }
    if (evt.key === 'Enter') {
      evt.preventDefault();
      const inputServer = evt.currentTarget.value.trim();
      if (inputServer) onServerChange(inputServer);
    }
  };

  const handleServerSelect: MouseEventHandler<HTMLButtonElement> = (evt) => {
    const selectedServer = evt.currentTarget.getAttribute('data-server');
    if (selectedServer) {
      onServerChange(selectedServer);
    }
    setServerMenuAnchor(undefined);
  };

  const handleOpenServerMenu: MouseEventHandler<HTMLElement> = (evt) => {
    const target = evt.currentTarget.parentElement ?? evt.currentTarget;
    setServerMenuAnchor(target.getBoundingClientRect());
  };

  return (
    <Input
      ref={serverInputRef}
      style={{ paddingRight: config.space.S200 }}
      variant={allowCustomServer ? 'Background' : 'Surface'}
      outlined
      defaultValue={server}
      onChange={handleServerChange}
      onKeyDown={handleKeyDown}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      size="500"
      readOnly={!allowCustomServer}
      onClick={allowCustomServer ? undefined : handleOpenServerMenu}
      // disable auto-capitalization, which is usually undesirable for server name input.
      autoCapitalize="off"
      // disable spell-check and auto-correction
      spellCheck={false}
      autoCorrect="off"
      after={
        serverList.length === 0 || (serverList.length === 1 && !allowCustomServer) ? undefined : (
          <PopOut
            anchor={serverMenuAnchor}
            position="Bottom"
            align="End"
            offset={4}
            content={
              <FocusTrap
                focusTrapOptions={{
                  initialFocus: false,
                  onDeactivate: () => setServerMenuAnchor(undefined),
                  clickOutsideDeactivates: true,
                  isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
                  isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
                  escapeDeactivates: stopPropagation,
                }}
              >
                <Menu>
                  <Header size="300" style={{ padding: `0 ${config.space.S200}` }}>
                    <Text size="L400">Homeserver List</Text>
                  </Header>
                  <div style={{ padding: config.space.S100, paddingTop: 0 }}>
                    {serverList?.map((serverName) => (
                      <MenuItem
                        key={serverName}
                        radii="300"
                        aria-pressed={serverName === server}
                        data-server={serverName}
                        onClick={handleServerSelect}
                      >
                        <Text>{serverName}</Text>
                      </MenuItem>
                    ))}
                  </div>
                </Menu>
              </FocusTrap>
            }
          >
            <IconButton
              onClick={handleOpenServerMenu}
              variant={allowCustomServer ? 'Background' : 'Surface'}
              size="300"
              aria-pressed={!!serverMenuAnchor}
              radii="300"
            >
              {composerIcon(CaretDown)}
            </IconButton>
          </PopOut>
        )
      }
    />
  );
}
